import type { OfferContentDto, OfferListFilter, OfferListItem, OfferVersionDto } from '@fos/shared/contracts/offers';
import { OFFER_VERSION_STATE_LABELS } from '@fos/shared/labels';
import { formatDateShort, formatDuration, formatMoneyAuto, formatRate } from '../../lib/format';

/** Numer oferty w UI: „#12”. */
export function offerNumberText(n: number): string {
  return `#${n}`;
}

/** Godziny oferty: „40 h”, pakiet: „Pakiet 20 h”, bez szacunku: null. */
export function offerHoursText(o: { estimatedSeconds: number | null; package: string | null }): string | null {
  if (!o.estimatedSeconds) return o.package ? 'Pakiet godzin' : null;
  const hours = formatDuration(o.estimatedSeconds);
  return o.package ? `Pakiet ${hours}` : hours;
}

/** Wartość netto w walucie oferty („6 000 zł”, „1 200 EUR”). */
export function offerMoneyText(amount: string, currency: string): string {
  if (currency === 'PLN') return formatMoneyAuto(amount);
  return formatMoneyAuto(amount).replace('zł', currency);
}

/** Stawka w walucie oferty („150 zł/h”). */
export function offerRateText(rate: string, currency: string): string {
  if (currency === 'PLN') return formatRate(rate);
  return formatRate(rate).replace('zł', currency);
}

/** Podpis wersji: „Wersja 2 · robocza”, „Wersja 1 · wysłana 3.10”. */
export function versionText(v: Pick<OfferVersionDto, 'versionNo' | 'state' | 'sentAt'>): string {
  const state = OFFER_VERSION_STATE_LABELS[v.state];
  const date = v.state === 'sent' && v.sentAt ? ` ${formatDateShort(v.sentAt)}` : '';
  return `Wersja ${v.versionNo} · ${state}${date}`;
}

/** Linia opisu w liście ofert: „REA Nieruchomości · #12 · wersja 2”. */
export function offerRowDescription(o: OfferListItem, withClient: boolean): string {
  return [withClient ? o.client.name : null, offerNumberText(o.number), `wersja ${o.currentVersion}`]
    .filter(Boolean)
    .join(' · ');
}

/** Adres strony oferty. */
export function offerHref(id: string): string {
  return `/oferty/${id}`;
}

/** Zakładki listy w adresie (`?filtr=`). */
export const FILTER_SLUGS: Record<OfferListFilter, string> = {
  open: 'otwarte',
  accepted: 'przyjete',
  rejected: 'odrzucone',
  all: 'wszystkie',
};

export function filterFromSlug(slug: string | null): OfferListFilter {
  const found = (Object.entries(FILTER_SLUGS) as [OfferListFilter, string][]).find(([, s]) => s === slug);
  return found ? found[0] : 'open';
}

/** Czy treść wersji jest pusta (nowa oferta przed uzupełnieniem). */
export function isContentEmpty(c: OfferContentDto): boolean {
  return [c.scope, c.result, c.exclusions, c.assumptions, c.terms, c.examples ?? ''].every((s) => s.trim() === '');
}
